import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
  Stack,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { useMemo, useState } from 'react';
import MarkupDetailsModalTable from './markup-details-modal-Table';
import ContingencyDetailsModalTable from './contingency-details-modal-Table';
import EscalationDetailsModalTable from './escalation-details-modal-Table';
import TaxDetailsModalTable from './tax-details-modal-Table';
import {
  CalculateContingencyDetails,
  CalculateDiscountDetails,
  CalculateEscalationDetails,
  CalculateTaxDetails,
} from '../utils/details-model-calculations';
import { ca } from 'date-fns/locale';
import DiscountDetailsModalTable from './discount-details-modal-Table';

const DetailsModal = ({
  type,
  currentMultiply,
  isOpen,
  handleClose,
  formData,
  setValue,
  getValues,
  isDetailedResource,
}) => {
  const {
    labor_cost,
    material_cost,
    equipment_cost,
    subcontractor_cost,
    other_cost,
    markupDetails,
    contingencyDetails,
    escalationDetails,
    taxDetails,
    discountDetails,
  } = formData;

  const multiplyOn = getValues(`${currentMultiply}`);

  const [calcBreakDown, setCalcBreakDown] = useState(false);

  const tableMap = {
    Markup: MarkupDetailsModalTable,
    Contingency: ContingencyDetailsModalTable,
    Escalation: EscalationDetailsModalTable,
    Tax: TaxDetailsModalTable,
    Discount: DiscountDetailsModalTable,
  };

  const TableComponent = tableMap[type];

  const tableData = {
    labor_cost: Number(labor_cost) || 0,
    material_cost: Number(material_cost) || 0,
    equipment_cost: Number(equipment_cost) || 0,
    subcontractor_cost: Number(subcontractor_cost) || 0,
    other_cost: Number(other_cost) || 0,
    markupDetails,
    contingencyDetails,
    escalationDetails,
    taxDetails,
    discountDetails,
  };

  const handleCalculation = () => {
    const calculations = {
      Contingency: () => CalculateContingencyDetails(formData, setValue, markupDetails.markupCost),
      Escalation: () =>
        CalculateEscalationDetails(
          formData,
          setValue,
          markupDetails.markupCost,
          contingencyDetails.contingencyCost
        ),
      Tax: () =>
        CalculateTaxDetails(
          formData,
          setValue,
          markupDetails.markupCost,
          contingencyDetails.contingencyCost,
          escalationDetails.escalationCost
        ),
      Discount: () =>
        CalculateDiscountDetails(
          formData,
          setValue,
          markupDetails.markupCost,
          contingencyDetails.contingencyCost,
          escalationDetails.escalationCost,
          taxDetails.taxCost
        ),
    };

    calculations[type]?.();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxSizing: 'content-box',
        }}
      >
        <span
          className="cardColor"
          style={{ padding: '7px', marginLeft: '-25px', color: 'white', fontWeight: '700' }}
        >
          &nbsp;&nbsp; {type} Calculations Details &nbsp;&nbsp;&nbsp;&nbsp;
        </span>
        <IconButton onClick={handleClose}>
          <Iconify icon="mdi:close" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {type !== 'Escalation' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={calcBreakDown}
                onChange={() => setCalcBreakDown((prev) => !prev)}
              />
            }
            label={<Typography variant="body1">{type} Calculations Break Down</Typography>}
          />
        )}

        {multiplyOn.hasOwnProperty('markup') && (
          <>
            {type !== 'Escalation' && <Divider />}

            <Stack spacing={1} sx={{ mt: 2 }}>
              <Typography variant="body1">Select to multiply on</Typography>

              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={<Checkbox checked={true} disabled />}
                  label={<Typography variant="body1">Direct Cost</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={!formData.markup}
                      checked={formData.markup && multiplyOn.markup}
                      onChange={() => {
                        setValue(`${currentMultiply}.markup`, !multiplyOn.markup);
                        handleCalculation();
                      }}
                    />
                  }
                  label={<Typography variant="body1">Markup Cost</Typography>}
                />
                {multiplyOn.hasOwnProperty('contingency') && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={!formData.contingency}
                        checked={formData.contingency && multiplyOn.contingency}
                        onChange={() => {
                          setValue(`${currentMultiply}.contingency`, !multiplyOn.contingency);
                          handleCalculation();
                        }}
                      />
                    }
                    label={<Typography variant="body1">Contingency Cost</Typography>}
                  />
                )}
                {multiplyOn.hasOwnProperty('escalation') && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={!formData.escalation}
                        checked={formData.escalation && multiplyOn.escalation}
                        onChange={() => {
                          setValue(`${currentMultiply}.escalation`, !multiplyOn.escalation);
                          handleCalculation();
                        }}
                      />
                    }
                    label={<Typography variant="body1">Escalation Cost</Typography>}
                  />
                )}
                {multiplyOn.hasOwnProperty('tax') && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={!formData.tax}
                        checked={formData.tax && multiplyOn.tax}
                        onChange={() => {
                          setValue(`${currentMultiply}.tax`, !multiplyOn.tax);
                          handleCalculation();
                        }}
                      />
                    }
                    label={<Typography variant="body1">Tax Cost</Typography>}
                  />
                )}
              </Stack>
            </Stack>
          </>
        )}

        <TableComponent
          type={type}
          calcBreakDown={calcBreakDown}
          tableData={tableData}
          multiplyOn={multiplyOn}
          formData={formData}
          hideDetails={!isDetailedResource}
        />
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetailsModal;
